/** #434 (SPEC-434): small pure helpers for the grid pages. */
import type {
  GridCompareStatus,
  GridRowFilters,
  GridVersionDetail,
} from '@/services/commissionGrids';

export const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  rejected: 'Rejected',
  ready: 'Awaiting publish',
  active: 'Live',
  withdrawn: 'Withdrawn',
};

export const COMPARE_LABELS: Record<GridCompareStatus, string> = {
  changed: 'Changed',
  basis_changed: 'Basis changed',
  region_changed: 'Region changed',
  added: 'Added',
  removed: 'Removed',
};

export const COMPARE_ORDER: GridCompareStatus[] = [
  'changed',
  'basis_changed',
  'region_changed',
  'added',
  'removed',
];

export function statusVariant(status: string): 'success' | 'warning' | 'error' | 'default' {
  if (status === 'active') return 'success';
  if (status === 'ready') return 'warning';
  if (status === 'rejected') return 'error';
  return 'default';
}

/** Only the broker admin of the grid's broker acts on a version (country and super admin read). */
export function canWriteGrids(role: string | undefined | null): boolean {
  return role === 'broker_admin';
}

export function versionActions(version: Pick<GridVersionDetail, 'status'>, canWrite: boolean) {
  return {
    activate: canWrite && version.status === 'ready',
    retire: canWrite && version.status === 'active',
    discard: canWrite && version.status === 'ready',
  };
}

export function liveVersion<T extends { status: string; effective_from: string }>(
  versions: T[]
): T | undefined {
  return versions
    .filter((v) => v.status === 'active')
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];
}

/** Default compare pair for a grid: live vs the version before it (newest first list). */
export function defaultComparePair(
  versions: GridVersionDetail[]
): { from: string; to: string } | null {
  const usable = versions.filter(
    (v) => v.status === 'active' || v.status === 'withdrawn' || v.status === 'ready'
  );
  if (usable.length < 2) return null;
  const live = usable.find((v) => v.status === 'active') ?? usable[0];
  const previous = usable.find((v) => v.version_no < live.version_no);
  return previous ? { from: previous.id, to: live.id } : null;
}

export function activeFilterCount(filters: GridRowFilters): number {
  return Object.values(filters).filter((v) => v !== undefined && v !== '').length;
}

export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
